import {
  Layout,
  Menu,
  Avatar,
  Badge,
  Drawer,
  Dropdown,
  Grid,
  Typography,
  Space,
  Button,
  App as AntdApp,
  theme,
} from "antd";
import {
  CalendarOutlined,
  ShoppingCartOutlined,
  TeamOutlined,
  ScissorOutlined,
  AppstoreOutlined,
  GoldOutlined,
  DollarOutlined,
  BarChartOutlined,
  LogoutOutlined,
  UserOutlined,
  SettingOutlined,
  KeyOutlined,
  ContactsOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  MenuOutlined,
  AuditOutlined,
  IdcardOutlined,
  WalletOutlined,
  BulbOutlined,
  CheckOutlined,
} from "@ant-design/icons";
import type { Permission } from "@salon/shared";
import { Link, Outlet, useLocation, useNavigate } from "react-router-dom";
import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useAuth } from "../../stores/auth";
import { useUI } from "../../stores/ui";
import { useThemeMode, type ThemeMode } from "../../stores/theme";
import { api, unwrap } from "../../lib/api";
import ChangePasswordModal from "./ChangePasswordModal";

type MenuCounts = {
  pendingAppointmentsAll: number;
  pendingAppointmentsMine: number;
  draftOrders: number;
};

const { Sider, Header, Content } = Layout;
const { useBreakpoint } = Grid;

type MenuConfig = {
  key: string;
  to?: string;
  icon: React.ReactNode;
  label: string;
  permission?: Permission;
  // Chỉ hiển thị khi user đã được liên kết với employee (qua employeeId).
  // Dùng cho các trang "cá nhân" — tự lọc dữ liệu theo empId trong JWT.
  requireEmployee?: boolean;
  children?: { key: string; to: string; label: string }[];
};

// Menu structure — children = sub-tabs trong page (deep-link bằng ?tab=)
const MENU: MenuConfig[] = [
  {
    key: "/appointments",
    to: "/appointments",
    icon: <CalendarOutlined />,
    label: "Lịch hẹn",
    permission: "appointments.view",
  },
  {
    key: "/me/appointments",
    to: "/me/appointments",
    icon: <IdcardOutlined />,
    label: "Lịch hẹn của tôi",
    requireEmployee: true,
  },
  {
    key: "/me/commissions",
    to: "/me/commissions",
    icon: <WalletOutlined />,
    label: "Hoa hồng của tôi",
    requireEmployee: true,
  },
  { key: "/pos", to: "/pos", icon: <ShoppingCartOutlined />, label: "Thu ngân", permission: "pos.view" },
  { key: "/orders", to: "/orders", icon: <ContactsOutlined />, label: "Lịch sử đơn", permission: "orders.view" },
  {
    key: "/employees",
    to: "/employees",
    icon: <TeamOutlined />,
    label: "Nhân viên",
    permission: "employees.view",
    children: [
      { key: "/employees?tab=employees", to: "/employees?tab=employees", label: "Danh sách" },
      { key: "/employees?tab=groups", to: "/employees?tab=groups", label: "Nhóm" },
    ],
  },
  {
    key: "/customers",
    to: "/customers",
    icon: <UserOutlined />,
    label: "Khách hàng",
    permission: "customers.view",
    children: [
      { key: "/customers?tab=list", to: "/customers?tab=list", label: "Danh sách" },
      { key: "/customers?tab=groups", to: "/customers?tab=groups", label: "Nhóm" },
    ],
  },
  {
    key: "/services",
    to: "/services",
    icon: <ScissorOutlined />,
    label: "Dịch vụ",
    permission: "services.view",
    children: [
      { key: "/services?tab=list", to: "/services?tab=list", label: "Danh sách" },
      { key: "/services?tab=groups", to: "/services?tab=groups", label: "Nhóm" },
    ],
  },
  {
    key: "/products",
    to: "/products",
    icon: <AppstoreOutlined />,
    label: "Sản phẩm",
    permission: "products.view",
    children: [
      { key: "/products?tab=list", to: "/products?tab=list", label: "Danh sách" },
      { key: "/products?tab=groups", to: "/products?tab=groups", label: "Nhóm" },
    ],
  },
  {
    key: "/inventory",
    to: "/inventory",
    icon: <GoldOutlined />,
    label: "Kho",
    permission: "inventory.view",
    children: [
      { key: "/inventory?tab=stock", to: "/inventory?tab=stock", label: "Tồn kho" },
      { key: "/inventory?tab=txns", to: "/inventory?tab=txns", label: "Nhập-xuất" },
    ],
  },
  {
    key: "/commissions",
    to: "/commissions",
    icon: <DollarOutlined />,
    label: "Hoa hồng",
    permission: "commissions.view",
    children: [
      { key: "/commissions?tab=service", to: "/commissions?tab=service", label: "Theo dịch vụ" },
      { key: "/commissions?tab=product", to: "/commissions?tab=product", label: "Theo sản phẩm" },
    ],
  },
  {
    key: "/reports",
    to: "/reports",
    icon: <BarChartOutlined />,
    label: "Báo cáo",
    permission: "reports.view",
    children: [
      { key: "/reports?tab=dashboard", to: "/reports?tab=dashboard", label: "Tổng quan" },
      { key: "/reports?tab=revenue", to: "/reports?tab=revenue", label: "Doanh thu" },
      { key: "/reports?tab=item-revenue", to: "/reports?tab=item-revenue", label: "Doanh thu theo mục" },
      { key: "/reports?tab=employee", to: "/reports?tab=employee", label: "Hoa hồng NV" },
      { key: "/reports?tab=customers", to: "/reports?tab=customers", label: "Khách hàng" },
      { key: "/reports?tab=appointments", to: "/reports?tab=appointments", label: "Lịch hẹn" },
      { key: "/reports?tab=discounts", to: "/reports?tab=discounts", label: "Giảm giá" },
      { key: "/reports?tab=inventory", to: "/reports?tab=inventory", label: "Kho" },
    ],
  },
  { key: "/users", to: "/users", icon: <KeyOutlined />, label: "Tài khoản", permission: "users.view" },
  { key: "/audit-log", to: "/audit-log", icon: <AuditOutlined />, label: "Nhật ký", permission: "audit.view" },
  { key: "/settings", to: "/settings", icon: <SettingOutlined />, label: "Cài đặt", permission: "settings.view" },
];

const THEME_LABEL: Record<ThemeMode, string> = {
  system: "Theo thiết bị",
  light: "Sáng",
  dark: "Tối",
};

export default function AppShell() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, clear, hasPermission } = useAuth();
  const { sidebarCollapsed, toggleSidebar } = useUI();
  const { mode: themeMode, setMode: setThemeMode } = useThemeMode();
  const { token } = theme.useToken();
  const { message } = AntdApp.useApp();
  const [pwdOpen, setPwdOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Mobile = width < 992 (lg breakpoint của AntD).
  const screens = useBreakpoint();
  const isMobile = !screens.lg;

  // Auto-đóng drawer mobile khi navigate sang trang khác.
  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname, location.search]);

  const onLogout = async () => {
    // Best-effort: gọi server xóa session. Nếu fail (network, token expired) cũng
    // vẫn clear local state — user thấy logged out.
    try {
      await api.api.auth.logout.$post();
    } catch {
      /* ignore */
    }
    clear();
    message.success("Đã đăng xuất");
    navigate("/login");
  };

  // Counts cho badge sidebar — refresh mỗi 60s + khi tab focus.
  const { data: counts } = useQuery({
    queryKey: ["menu-counts"],
    queryFn: () => unwrap<MenuCounts>(api.api.menu.counts.$get()),
    enabled: !!user,
    staleTime: 30_000,
    refetchInterval: 60_000,
    refetchOnWindowFocus: true,
  });

  const badgeFor: Record<string, number> = {
    "/appointments": counts?.pendingAppointmentsAll ?? 0,
    "/me/appointments": counts?.pendingAppointmentsMine ?? 0,
    "/orders": counts?.draftOrders ?? 0,
  };

  const filteredMenu = useMemo(() => {
    return MENU.filter((m) => {
      if (m.permission && !hasPermission(m.permission)) return false;
      if (m.requireEmployee && !user?.employeeId) return false;
      return true;
    }).map((m) => {
      const count = badgeFor[m.key] ?? 0;
      const iconWithBadge =
        count > 0 ? (
          <Badge dot offset={[-2, 2]}>
            {m.icon}
          </Badge>
        ) : (
          m.icon
        );
      if (m.children) {
        return {
          key: m.key,
          icon: iconWithBadge,
          label: m.label,
          children: m.children.map((c) => ({
            key: c.key,
            label: <Link to={c.to}>{c.label}</Link>,
          })),
        };
      }
      return {
        key: m.key,
        icon: iconWithBadge,
        label: (
          <Link
            to={m.to!}
            style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}
          >
            <span>{m.label}</span>
            {count > 0 && (
              <Badge
                count={count}
                size="small"
                overflowCount={99}
                style={{ backgroundColor: token.colorError }}
              />
            )}
          </Link>
        ),
      };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, counts]);

  // Selected key gồm cả ?tab= để highlight đúng sub-menu
  const currentPath = location.pathname;
  const currentTab = new URLSearchParams(location.search).get("tab");
  const selectedKey = currentTab ? `${currentPath}?tab=${currentTab}` : currentPath;
  const openParent = MENU.find((m) => m.children && currentPath.startsWith(m.key))?.key;

  // Logo + menu — share giữa Sider (desktop) và Drawer (mobile).
  const sidebarContent = (collapsed: boolean) => (
    <>
      <div
        style={{
          height: 56,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: collapsed ? 0 : "0 12px 0 16px",
          color: "#fff",
          fontWeight: 700,
          fontSize: collapsed ? 14 : 18,
          letterSpacing: 0.5,
          whiteSpace: "nowrap",
          overflow: "hidden",
        }}
      >
        {!collapsed && <span>💇 Salon</span>}
        {!isMobile && (
          <Button
            type="text"
            icon={
              collapsed ? (
                <MenuUnfoldOutlined style={{ color: "#fff" }} />
              ) : (
                <MenuFoldOutlined style={{ color: "#fff" }} />
              )
            }
            onClick={toggleSidebar}
            style={{ width: collapsed ? "100%" : "auto" }}
          />
        )}
      </div>
      <Menu
        theme="dark"
        mode="inline"
        items={filteredMenu}
        selectedKeys={[selectedKey, currentPath]}
        defaultOpenKeys={openParent ? [openParent] : []}
      />
    </>
  );

  const userDropdownItems = [
    {
      key: "theme",
      icon: <BulbOutlined />,
      label: "Chế độ hiển thị",
      children: (["system", "light", "dark"] as ThemeMode[]).map((m) => ({
        key: `theme-${m}`,
        label: THEME_LABEL[m],
        icon: themeMode === m ? <CheckOutlined /> : <span style={{ width: 14 }} />,
        onClick: () => setThemeMode(m),
      })),
    },
    {
      key: "change-password",
      icon: <KeyOutlined />,
      label: "Đổi mật khẩu",
      onClick: () => setPwdOpen(true),
    },
    { type: "divider" as const },
    {
      key: "logout",
      icon: <LogoutOutlined />,
      label: "Đăng xuất",
      onClick: onLogout,
    },
  ];

  return (
    <Layout style={{ minHeight: "100vh" }}>
      {/* Desktop: Sider cố định */}
      {!isMobile && (
        <Sider
          theme="dark"
          width={232}
          collapsed={sidebarCollapsed}
          collapsedWidth={64}
          trigger={null}
        >
          {sidebarContent(sidebarCollapsed)}
        </Sider>
      )}

      {/* Mobile: Drawer trượt từ trái khi bấm hamburger */}
      {isMobile && (
        <Drawer
          placement="left"
          open={mobileMenuOpen}
          onClose={() => setMobileMenuOpen(false)}
          closable={false}
          styles={{
            body: { padding: 0, background: token.colorBgLayout },
            header: { display: "none" },
            wrapper: { width: 260, boxShadow: "none" },
          }}
        >
          {sidebarContent(false)}
        </Drawer>
      )}

      <Layout>
        <Header
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            borderBottom: `1px solid ${token.colorBorder}`,
            padding: "0 16px",
            gap: 8,
          }}
        >
          {isMobile ? (
            <Button
              type="text"
              icon={<MenuOutlined />}
              onClick={() => setMobileMenuOpen(true)}
              aria-label="Mở menu"
            />
          ) : (
            <span />
          )}

          <Dropdown menu={{ items: userDropdownItems }} trigger={["click"]}>
            <Space style={{ cursor: "pointer" }}>
              <Avatar icon={<UserOutlined />} />
              {!isMobile && (
                <span>
                  {user?.username}{" "}
                  <Typography.Text type="secondary">({user?.role})</Typography.Text>
                </span>
              )}
            </Space>
          </Dropdown>
        </Header>
        <Content className="app-content">
          <Outlet />
        </Content>
      </Layout>
      <ChangePasswordModal open={pwdOpen} onClose={() => setPwdOpen(false)} />
    </Layout>
  );
}
